import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const API_BASE_URL = 'https://spaceportfolio-api.rajansudhan0.workers.dev';

const SettingsManager = ({ adminKey }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/settings`, { 
      cache: 'no-store',
      headers: { 'X-Admin-Key': adminKey }
    })
      .then(res => res.json())
      .then(data => {
        setSettings(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching settings:', err);
        setLoading(false);
      });
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    const formData = new FormData(e.target);
    const updates = {};
    
    // Parse hero_stats back to JSON array
    const labels = formData.getAll('stat_label');
    const values = formData.getAll('stat_value');
    const icons = formData.getAll('stat_icon');
    
    updates.hero_stats = labels
      .map((label, i) => ({
        label,
        value: values[i],
        icon: icons[i]
      }))
      .filter(stat => stat.label || stat.value); // Remove empty rows

    // Parse client_stats back to JSON array
    const clientMetrics = formData.getAll('client_stat_metric');
    const clientDescs = formData.getAll('client_stat_desc');
    
    updates.client_stats = clientMetrics
      .map((metric, i) => ({
        metric,
        description: clientDescs[i]
      }))
      .filter(stat => stat.metric || stat.description); // Remove empty rows

    updates.availability_status = formData.get('availability_status');
    updates.calendly_url = formData.get('calendly_url');
    updates.about_hero_pic = formData.get('about_hero_pic');
    updates.about_hero_desc = formData.get('about_hero_desc');
    updates.hero_description = formData.get('hero_description');
    
    const clientNames = formData.getAll('client_logo_name');
    const clientLogos = formData.getAll('client_logo_url');
    const clientIndustries = formData.getAll('client_logo_industry');
    
    updates.client_logos = clientNames
      .map((name, i) => ({
        name,
        logo: clientLogos[i],
        industry: clientIndustries[i]
      }))
      .filter(c => c.name || c.logo);
    
    // Global Contacts
    updates.contact_email = formData.get('contact_email');
    updates.contact_phone = formData.get('contact_phone');
    updates.social_linkedin = formData.get('social_linkedin');
    updates.social_github = formData.get('social_github');
    updates.social_orcid = formData.get('social_orcid');
    updates.contact_location = formData.get('contact_location');

    try {
      const res = await fetch(`${API_BASE_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': adminKey
        },
        body: JSON.stringify(updates)
      });

      if (res.ok) {
        setMessage({ type: 'success', text: 'Settings updated successfully!' });
        setTimeout(() => setMessage(null), 5000);
        // Refresh local state
        setSettings(prev => ({ ...prev, ...updates }));
      } else {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Update failed');
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const addStat = () => {
    setSettings(prev => ({
      ...prev,
      hero_stats: [...(prev?.hero_stats || []), { label: '', value: '', icon: '' }]
    }));
  };

  const removeStat = (index) => {
    setSettings(prev => ({
      ...prev,
      hero_stats: (prev?.hero_stats || []).filter((_, i) => i !== index)
    }));
  };

  const addClientLogo = () => {
    setSettings(prev => ({
      ...prev,
      client_logos: [...(prev?.client_logos || []), { name: '', logo: '', industry: '' }]
    }));
  };

  const removeClientLogo = (index) => {
    setSettings(prev => ({
      ...prev,
      client_logos: (prev?.client_logos || []).filter((_, i) => i !== index)
    }));
  };

  if (loading) return <div className="animate-pulse space-y-8">
    <div className="h-40 bg-white/5 rounded-2xl"></div>
    <div className="h-40 bg-white/5 rounded-2xl"></div>
  </div>;

  // Use current settings stats or default empty slots if none exist
  const heroStats = Array.isArray(settings?.hero_stats) && settings.hero_stats.length > 0 
    ? settings.hero_stats 
    : [{ label: '', value: '', icon: '' }, { label: '', value: '', icon: '' }, { label: '', value: '', icon: '' }];

  const partnerLogos = Array.isArray(settings?.client_logos) && settings.client_logos.length > 0 
    ? settings.client_logos 
    : [
        { name: "NASA STEM Gateway", logo: "https://upload.wikimedia.org/wikipedia/commons/e/e5/NASA_logo.svg", industry: "STEM Education / Astronomy" },
        { name: "ISRO", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bd/Indian_Space_Research_Organisation_Logo.svg/450px-Indian_Space_Research_Organisation_Logo.svg.png", industry: "Space Research / Remote Sensing" },
        { name: "NARL (ISRO)", logo: "https://media.licdn.com/dms/image/v2/C510BAQEkpuR-jeugQg/company-logo_200_200/company-logo_200_200/0/1630614913323/national_atmospheric_research_laboratory_isro_logo?e=2147483647&v=beta&t=SyNoGSJQ0LSLd5hQlj68ktHTuzB9LIlhPvr8db1Trrc", industry: "Atmospheric Science / Space Research" },
        { name: "IAAC 2025", logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSdzkU2t5eY7f_i2nI_29XUVf3BpduM5Cj4_A&s", industry: "International Science Outreach" },
        { name: "Vigyan Prasar", logo: "https://niascomm.in/wp-content/uploads/2020/07/Vigyan-Prasar.jpg", industry: "Science Communication / Public Engagement" },
        { name: "SSPI", logo: "https://www.sspi.org/sites/default/files/sspi-logo.png", industry: "Satellite & Space Networking" }
      ];

  return (
    <form onSubmit={handleUpdate} className="space-y-8 pb-12">
      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-3 ${
          message.type === 'success' ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'
        }`}>
          <Icon name={message.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={20} />
          <span>{message.text}</span>
        </div>
      )}

      {/* Hero Stats */}
      <div className="bg-surface/50 border border-white/10 rounded-2xl p-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-heading font-bold text-xl flex items-center gap-2">
            <Icon name="Rocket" size={20} className="text-accent" />
            Hero Metrics
          </h3>
          <button 
            type="button"
            onClick={addStat}
            className="text-xs bg-accent/20 text-accent px-3 py-1.5 rounded-lg hover:bg-accent/30 transition-smooth flex items-center gap-1"
          >
            <Icon name="Plus" size={14} />
            Add Metric
          </button>
        </div>
        
        <p className="text-sm text-text-secondary mb-6 italic">
          These stats appear in the card section at the very top of your portfolio (Hero Section). 
          Tip: Use names like "Rocket", "Target", or "Award" for icons.
        </p>
        
        <div className="space-y-4">
          {heroStats.map((stat, i) => (
            <div key={i} className="grid grid-cols-12 gap-4 p-4 bg-white/5 rounded-xl border border-white/5 relative group/item">
              <div className="col-span-4">
                <label className="block text-xs text-text-secondary mb-1">Label (Mandatory)</label>
                <input name="stat_label" defaultValue={stat.label} required placeholder="e.g. Projects Done" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent" />
              </div>
              <div className="col-span-4">
                <label className="block text-xs text-text-secondary mb-1">Value (Mandatory)</label>
                <input name="stat_value" defaultValue={stat.value} required placeholder="e.g. 50+" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent" />
              </div>
              <div className="col-span-3">
                <label className="block text-xs text-text-secondary mb-1">Lucide Icon</label>
                <input name="stat_icon" defaultValue={stat.icon} placeholder="Rocket" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent" />
              </div>
              <div className="col-span-1 flex items-end justify-center">
                <button 
                  type="button" 
                  onClick={() => removeStat(i)}
                  className="p-2 text-text-secondary hover:text-red-500 transition-smooth"
                >
                  <Icon name="Trash2" size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Client Stats */}
      <div className="bg-surface/50 border border-white/10 rounded-2xl p-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-heading font-bold text-xl flex items-center gap-2">
            <Icon name="BarChart" size={20} className="text-accent" />
            Client & Research Stats
          </h3>
          <button 
            type="button"
            onClick={() => setSettings(prev => ({ ...prev, client_stats: [...(prev?.client_stats || []), { metric: '', description: '' }] }))}
            className="text-xs bg-accent/20 text-accent px-3 py-1.5 rounded-lg hover:bg-accent/30 transition-smooth flex items-center gap-1"
          >
            <Icon name="Plus" size={14} />
            Add Stat
          </button>
        </div>
        
        <p className="text-sm text-text-secondary mb-6 italic">
          These stats appear below the Client Logos section.
        </p>
        
        <div className="grid md:grid-cols-2 gap-4">
          {(settings?.client_stats || [
            { metric: "2+", description: "Agency Internship Experiences" },
            { metric: "8+", description: "Research Domains Explored" }
          ]).map((stat, i) => (
            <div key={i} className="flex gap-4 p-4 bg-white/5 rounded-xl border border-white/5 relative group/item">
              <div className="flex-1 space-y-3">
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Metric Value (e.g. "2+")</label>
                  <input name="client_stat_metric" defaultValue={stat.metric} required className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent" />
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Description</label>
                  <input name="client_stat_desc" defaultValue={stat.description} required className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent" />
                </div>
              </div>
              <div className="flex items-center justify-center pt-5">
                <button 
                  type="button" 
                  onClick={() => setSettings(prev => ({ ...prev, client_stats: (prev?.client_stats || []).filter((_, idx) => idx !== i) }))}
                  className="p-2 text-text-secondary hover:text-red-500 transition-smooth"
                >
                  <Icon name="Trash2" size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Global Contact Details */}
      <div className="bg-surface/50 border border-white/10 rounded-2xl p-6">
        <h3 className="font-heading font-bold text-xl mb-6 flex items-center gap-2">
          <Icon name="Globe" size={20} className="text-accent" />
          Global Contact & Social Links
        </h3>
        <p className="text-sm text-text-secondary mb-6 italic">
          These details sync directly across the Contact Page, Footer, Hero Sections, and everywhere else dynamically!
        </p>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Primary Email</label>
            <input name="contact_email" defaultValue={settings?.contact_email || 'sudhan.r.web@gmail.com'} className="w-full bg-background border border-white/10 rounded-xl px-4 py-3 text-text-primary outline-none focus:border-accent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Phone / WhatsApp</label>
            <input name="contact_phone" defaultValue={settings?.contact_phone || '+91-9940980978'} className="w-full bg-background border border-white/10 rounded-xl px-4 py-3 text-text-primary outline-none focus:border-accent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">LinkedIn Profile URL</label>
            <input name="social_linkedin" defaultValue={settings?.social_linkedin || 'https://www.linkedin.com/in/sudhan23082004/'} className="w-full bg-background border border-white/10 rounded-xl px-4 py-3 text-text-primary outline-none focus:border-accent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">GitHub Profile URL</label>
            <input name="social_github" defaultValue={settings?.social_github || 'https://github.com/Sudhan-23082004'} className="w-full bg-background border border-white/10 rounded-xl px-4 py-3 text-text-primary outline-none focus:border-accent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">ORCID Registration URL</label>
            <input name="social_orcid" defaultValue={settings?.social_orcid || 'https://orcid.org/0009-0005-7769-6379'} className="w-full bg-background border border-white/10 rounded-xl px-4 py-3 text-text-primary outline-none focus:border-accent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Location/Maps Link</label>
            <input name="contact_location" defaultValue={settings?.contact_location || ''} placeholder="https://maps.google.com/..." className="w-full bg-background border border-white/10 rounded-xl px-4 py-3 text-text-primary outline-none focus:border-accent" />
          </div>
        </div>
      </div>

      {/* Availability */}
      <div className="bg-surface/50 border border-white/10 rounded-2xl p-6">
        <h3 className="font-heading font-bold text-xl mb-6 flex items-center gap-2">
          <Icon name="UserCheck" size={20} className="text-accent" />
          General Info & About Hero
        </h3>
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Availability Status</label>
            <select 
              name="availability_status" 
              defaultValue={settings?.availability_status} 
              className="w-full bg-background border border-white/10 rounded-xl px-4 py-3 text-text-primary outline-none focus:border-accent"
            >
              <option value="available">Available for New Missions</option>
              <option value="busy">Busy with Current Research</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Calendly URL</label>
            <input 
              name="calendly_url" 
              defaultValue={settings?.calendly_url} 
              className="w-full bg-background border border-white/10 rounded-xl px-4 py-3 text-text-primary outline-none focus:border-accent" 
            />
          </div>
        </div>
        <div className="space-y-6 border-t border-white/10 pt-6">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Global Profile Picture (URL)</label>
            <input 
              name="about_hero_pic" 
              defaultValue={settings?.about_hero_pic || "/assets/images/Sudhan_2026_v3.png"} 
              className="w-full bg-background border border-white/10 rounded-xl px-4 py-3 text-text-primary outline-none focus:border-accent" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Homepage Hero Description</label>
            <textarea 
              name="hero_description" 
              defaultValue={settings?.hero_description || "I’m a Computer Science Engineer, Software Developer, and Space Technology Researcher specializing in Artificial Intelligence, Remote Sensing, Earth Observation, and Geospatial Analytics. I work on turning satellite and UAV data into real-world insights."} 
              className="w-full bg-background border border-white/10 rounded-xl px-4 py-3 text-text-primary outline-none focus:border-accent h-32" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">About Page Description</label>
            <textarea 
              name="about_hero_desc" 
              defaultValue={settings?.about_hero_desc || "I'm a passionate space science enthusiast and STEM educator, driven by curiosity and a mission to bridge technology with societal impact. From collaborating with NASA and ISRO to leading outreach for the International Astronomy & Astrophysics Competition, I thrive at the intersection of data, discovery, and education."} 
              className="w-full bg-background border border-white/10 rounded-xl px-4 py-3 text-text-primary outline-none focus:border-accent h-32" 
            />
          </div>
        </div>
      </div>

      {/* Partner Logos */}
      <div className="bg-surface/50 border border-white/10 rounded-2xl p-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-heading font-bold text-xl flex items-center gap-2">
            <Icon name="Users" size={20} className="text-accent" />
            Partner & Collaborator Logos
          </h3>
          <button 
            type="button"
            onClick={addClientLogo}
            className="text-xs bg-accent/20 text-accent px-3 py-1.5 rounded-lg hover:bg-accent/30 transition-smooth flex items-center gap-1"
          >
            <Icon name="Plus" size={14} />
            Add Partner Logo
          </button>
        </div>
        
        <p className="text-sm text-text-secondary mb-6 italic">
          Manage the partner and client logos displayed in the logo wall section of your homepage.
        </p>
        
        <div className="space-y-4">
          {partnerLogos.map((client, i) => (
            <div key={i} className="grid grid-cols-12 gap-4 p-4 bg-white/5 rounded-xl border border-white/5 relative group/item">
              <div className="col-span-4">
                <label className="block text-xs text-text-secondary mb-1">Partner Name (Mandatory)</label>
                <input name="client_logo_name" defaultValue={client.name} required placeholder="e.g. NASA STEM Gateway" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent" />
              </div>
              <div className="col-span-4">
                <label className="block text-xs text-text-secondary mb-1">Logo Image URL (Mandatory)</label>
                <input name="client_logo_url" defaultValue={client.logo} required placeholder="e.g. https://domain.com/logo.png" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent" />
              </div>
              <div className="col-span-3">
                <label className="block text-xs text-text-secondary mb-1">Industry / Category</label>
                <input name="client_logo_industry" defaultValue={client.industry} placeholder="e.g. Space Research" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent" />
              </div>
              <div className="col-span-1 flex items-end justify-center">
                <button 
                  type="button" 
                  onClick={() => removeClientLogo(i)}
                  className="p-2 text-text-secondary hover:text-red-500 transition-smooth"
                >
                  <Icon name="Trash2" size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="bg-accent hover:bg-accent/90 disabled:opacity-50 text-white font-bold px-8 py-3 rounded-xl transition-smooth shadow-lg shadow-accent/20 flex items-center gap-2"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          ) : (
            <Icon name="Save" size={20} />
          )}
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
    </form>
  );
};

export default SettingsManager;
